import partition from 'lodash/partition';
import pick from 'lodash/pick';
import pickBy from 'lodash/pickBy';

import {
  CohortGroupEntry,
  FiltersOperator,
  isFormulaEntry,
  isMetricEntry,
  MetricEntry,
  QueryBehaviorEntry,
  QueryMetricEntry,
  SelectorTypeDefinitions,
} from 'common/query-builder/query-entry-section/types';
import {createDefaultCohortMetricEntry} from 'common/report/util/cohorts/transformations/entries';
import {getInlineCustomPropertyId} from 'common/widgets/custom-property-modal/util';
import {ListOperator, NumberOperator, StringOperator} from 'common/widgets/property-filter-menu/models/types';
import {PRIMITIVE_MATH_TYPES, PropertyType, ResourceType} from 'common/report/constants';
import {CustomPropertyResourceType} from 'common/report/stores/custom-properties';
import {
  Behavior,
  BehavioralAttributes,
  CustomProperty,
  Segmentation,
  SegmentationPropertyFields,
} from 'common/types/reports/query';
import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {SUPPORTED_BEHAVIORAL_AGGREGATIONS} from 'common/report/util/cohorts/utils';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {PRESET_DATE_RANGE_TO_DATE_RANGE_PARAMS, PresetDateRange} from 'common/widgets/date-range-picker/models/presets';
import {Cohort} from 'common/query-builder/cohorts-query-builder/models/cohort';
import {TOP_PEOPLE} from 'common/query-builder/query-block/properties-menu/constants';
import {isFirstTimeEventFilter} from 'common/widgets/property-filter-menu/models/utils';
import {InlineCohortSegment} from 'common/query-builder/query-block/segment-menu/types';
import {dataGroupIDsEqual} from 'common/util';

type FiltersByResourceType = {
  [type in ResourceType]: PropertyFilter[];
};

function hasUniformResourceTypes(filters: PropertyFilter[]) {
  return filters?.length <= 1 || new Set(filters.map((filter) => filter.resourceType)).size === 1;
}

function groupPropertyFiltersByResourceType(filters: PropertyFilter[]): FiltersByResourceType {
  const groups = {
    [ResourceType.Event]: [],
    [ResourceType.Formula]: [],
    [ResourceType.User]: [],
    [ResourceType.Cohort]: [],
    [ResourceType.Other]: [],
  };

  for (const filter of filters) {
    groups[filter.resourceType].push(filter);
  }

  return groups;
}

function getBehavioralFilterFromQueryBehaviorEntry(
  entry: QueryBehaviorEntry,
  label = ``,
  frequency = new PropertyFilter({
    propertyType: PropertyType.Number,
    filterOperator: NumberOperator.AtLeast,
    filterValue: 1,
    resourceType: ResourceType.User,
  }),
): PropertyFilter {
  const customProperty: CustomProperty = {
    name: label,
    behavior: pick(entry, BehavioralAttributes) as Behavior,
    description: ``,
    referencedBy: [],
    referencedDirectlyBy: [],
    referencedRawEventProperties: [],
    propertyType: frequency.propertyType,
    resourceType: CustomPropertyResourceType.People,
  };

  const customPropertyId = getInlineCustomPropertyId(null);

  return new PropertyFilter({
    ...frequency.getAttributes(),
    customProperty,
    customPropertyId,
    resourceType: ResourceType.User,
  });
}

function getCohortEntriesFromBehavior(
  behavior: QueryBehaviorEntry,
  {
    label = ``,
    filters = [],
    filtersOperator = FiltersOperator.And,
    entryFiltersOperator = FiltersOperator.And,
    behavioralFiltersOperator = FiltersOperator.And,
    groupingOperator = FiltersOperator.And,
    frequency,
    frequencyExclusive,
    dataGroupId,
  }: {
    label?: string;
    filters?: PropertyFilter[];
    filtersOperator?: FiltersOperator;
    entryFiltersOperator?: FiltersOperator;
    behavioralFiltersOperator?: FiltersOperator;
    groupingOperator?: FiltersOperator.And | FiltersOperator.Or;
    frequency?: PropertyFilter;
    frequencyExclusive?: boolean;
    dataGroupId?: string;
  } = {},
): CohortGroupEntry[] {
  const {event: eventFilters, user: userFilters, cohort: cohortFilters} = groupPropertyFiltersByResourceType(filters);

  /**
   * Whenever a metric is performing data group joins, group filters need to be joined on events if the query is not counting
   * uniques by the same data group, example:
   *
   * Analyze Uniques by User
   * Event A with filters Company.ARR > 0
   *
   * The Company.ARR property is joined by the group key on the event, when looking at different data groups these cannot be global filters
   * we force them to join at the event, when changing "Analyze Uniques by Company" these can be global filters
   */
  const [globalUserFilters, forcedInlineFilters] = partition(userFilters, (filter) =>
    dataGroupIDsEqual(filter.dataGroupId, dataGroupId),
  );

  let convertedGlobalUserFilters = null;

  // Need to convert Frequency behavior filters into the old format that the Users page understands
  if (globalUserFilters.some((filter) => filter.behavior)) {
    convertedGlobalUserFilters = globalUserFilters.map((filter) =>
      filter.behavior
        ? getBehavioralFilterFromQueryBehaviorEntry(
            {
              ...filter.behavior,
              dateRange: behavior.dateRange,
            } as QueryBehaviorEntry,
            label,
            new PropertyFilter({
              propertyType: filter.propertyType,
              filterOperator: filter.filterOperator,
              filterValue: filter.filterValue,
              resourceType: filter.resourceType,
            }),
          )
        : filter,
    );
  }

  let convertedForcedInlineFilters = null;

  if (forcedInlineFilters.some((filter) => filter.behavior)) {
    convertedForcedInlineFilters = forcedInlineFilters.map((filter) =>
      filter.behavior
        ? getBehavioralFilterFromQueryBehaviorEntry(
            {
              ...filter.behavior,
              dateRange: behavior.dateRange,
            } as QueryBehaviorEntry,
            label,
            new PropertyFilter({
              propertyType: filter.propertyType,
              filterOperator: filter.filterOperator,
              filterValue: filter.filterValue,
              resourceType: filter.resourceType,
            }),
          )
        : filter,
    );
  }

  const behaviorInlineFilters = [
    ...behavior.filters,
    ...eventFilters,
    ...(convertedForcedInlineFilters ?? forcedInlineFilters),
  ];

  let behaviorFilters = [
    getBehavioralFilterFromQueryBehaviorEntry(
      {
        ...behavior,
        filtersOperator,
        filters: behaviorInlineFilters,
      },
      label,
      frequency,
    ),
  ];

  // Apply rational frequencies as two behavioral filters if frequency is exclusive (e.g. distribution on Insights)
  if (frequencyExclusive && Array.isArray(frequency.filterValue) && frequency.filterValue?.length > 1) {
    behaviorFilters = [
      getBehavioralFilterFromQueryBehaviorEntry(
        {
          ...behavior,
          filtersOperator,
          filters: behaviorInlineFilters,
        },
        label,
        new PropertyFilter({
          ...frequency.getAttributes(),
          filterOperator: NumberOperator.AtLeast,
          filterValue: frequency.filterValue[0],
        }),
      ),
      getBehavioralFilterFromQueryBehaviorEntry(
        {
          ...behavior,
          filtersOperator,
          filters: behaviorInlineFilters,
        },
        label,
        new PropertyFilter({
          ...frequency.getAttributes(),
          filterOperator: NumberOperator.LessThan,
          filterValue: frequency.filterValue[1],
        }),
      ),
    ];
  }

  const entry = {
    ...createDefaultCohortMetricEntry(),
    filters: [...(convertedGlobalUserFilters ?? globalUserFilters), ...behaviorFilters],
    filtersOperator: entryFiltersOperator,
    behavioralFiltersOperator,
    groupingOperator,
  };

  return [
    entry,
    ...cohortFilters.map((filter) => ({
      ...createDefaultCohortMetricEntry(),
      filters: [filter],
      filtersOperator: FiltersOperator.And,
    })),
  ];
}

export function createNestedCohort(cohorts: Cohort[]) {
  const cohort = new Cohort();
  // Prevents the create-entity-modal from complaining about missing createdBy
  cohort.createdBy = {
    name: null,
    email: null,
    id: null,
  };
  cohort.dataGroupId = cohorts[0].dataGroupId;

  /** if defining a single cohort, we expose the full definition
   * unlike multiple cohorts where we OR these are cohort filters */
  if (cohorts.length === 1) {
    return cohorts[0];
  }

  const createCohortFilter = (cohort: Cohort) =>
    new PropertyFilter({
      resourceType: ResourceType.Cohort,
      propertyType: PropertyType.List,
      propertyDefaultType: PropertyType.List,
      filterOperator: ListOperator.Contains,
      filterValue: [
        {
          cohort: {
            raw_cohort: cohort.serialize(),
            negated: false,
            name: cohort.name,
          },
        } as InlineCohortSegment,
      ],
    });

  cohort.groups = cohorts.map((cohort) => ({
    ...createDefaultCohortMetricEntry(),
    filters: [createCohortFilter(cohort)],
    groupingOperator: FiltersOperator.Or,
  }));

  return cohort;
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export function translateMetricToCohort(
  entry: QueryMetricEntry | MetricEntry,
  {
    name,
    dateRange = new DateRange(PRESET_DATE_RANGE_TO_DATE_RANGE_PARAMS[PresetDateRange.Last30Days]),
    frequency,
    frequencyExclusive = false,
    keepUserAggregation = false,
    filters: segmentFilters = [],
  }: {
    name?: string; // name of the clause
    dateRange?: DateRange;
    filters?: PropertyFilter[]; // Segment filters are anded together
    frequencyExclusive?: boolean;
    // Indicates whether user aggregation should be simplified to is set
    keepUserAggregation?: boolean;
    frequency?: PropertyFilter;
  } = {},
): Cohort {
  const cohort = new Cohort();
  // TODO: Make translateMetricToCohort work for metric entry
  if (isMetricEntry(entry) || isFormulaEntry(entry)) {
    return cohort;
  }

  let {
    event,
    aggregationOperatorPerUser,
    property,
    filters: entryFilters,
    filtersOperator = FiltersOperator.And,
    dataGroupId,
  } = entry;

  const {label} = event;
  let additionalFilters = segmentFilters;
  const [behavioralFilters, inlineFilters] = partition(entryFilters, isFirstTimeEventFilter);
  const hasFirstTimeFilter = behavioralFilters.length > 0;

  // prevents a case where the entry has `or` filters with only one entry
  filtersOperator =
    filtersOperator === FiltersOperator.Or && inlineFilters.length <= 1 ? FiltersOperator.And : filtersOperator;

  // Prevents the create-entity-modal from complaining about missing createdBy
  cohort.createdBy = {
    name: null,
    email: null,
    id: null,
  };
  cohort.dataGroupId = dataGroupId;
  cohort.name = name;
  cohort.unsavedId = name;

  let behavior: QueryBehaviorEntry = {
    type: SelectorTypeDefinitions.Behavior,
    dateRange,
    event,
    filters: hasFirstTimeFilter ? behavioralFilters : [],
    filtersOperator,
    aggregationOperator: PRIMITIVE_MATH_TYPES.TOTAL,
    property: null,
    aggregationOperatorPerUser: null,
  };

  /** handles total per user entries  */
  if (property && SUPPORTED_BEHAVIORAL_AGGREGATIONS.includes(aggregationOperatorPerUser)) {
    /** for entries with anything other that distribution, we translate to "did event at least once" */
    if (!keepUserAggregation) {
      additionalFilters = [
        ...additionalFilters,
        new PropertyFilter({
          propertyType: PropertyType.String,
          propertyDefaultType: PropertyType.String,
          filterOperator: StringOperator.Set,
          propertyName: property.value,
          resourceType: property.resourceType,
        }),
      ];
    } else {
      behavior = {
        ...behavior,
        aggregationOperator: aggregationOperatorPerUser,
        property,
      };
    }
  }

  /** events with 1st time filters and global filters which apply after the fact of event occurrence, we define those as first value of */
  if (hasFirstTimeFilter && additionalFilters.length) {
    additionalFilters = additionalFilters.map((filter) => {
      if (filter.resourceType !== ResourceType.Event) {
        return filter;
      }

      // instead of specifying a filter like `Country = United States`, we convert these to
      // did Event Name with First Value of Country = United States, a behavioral filter
      return getBehavioralFilterFromQueryBehaviorEntry(
        {
          ...behavior,
          aggregationOperator: PRIMITIVE_MATH_TYPES.FIRST_VALUE,
          property: {
            label: null,
            resourceType: ResourceType.Event,
            propertyDefaultType: filter.propertyDefaultType ?? filter.propertyType,
            type: filter.propertyType,
            value: filter.propertyName,
            ...pickBy(
              filter,
              (value, key) =>
                SegmentationPropertyFields.has(key as keyof Segmentation) && key !== `propertyName` && Boolean(value),
            ),
          },
          filtersOperator,
          filters: inlineFilters.filter((filter) => filter.resourceType === ResourceType.Event),
        },
        label,
        filter,
      );
    });
  }

  /** profile mode without aggregations translates directly to a cohort definition with filters */
  if (event.value === TOP_PEOPLE.value) {
    const groups = [];
    if (filtersOperator === FiltersOperator.Or) {
      for (const inlineFilter of inlineFilters) {
        groups.push({
          ...createDefaultCohortMetricEntry(),
          filters: [inlineFilter, ...additionalFilters],
          filtersOperator: FiltersOperator.And,
          groupingOperator: FiltersOperator.Or,
        });
      }
    } else {
      groups.push({
        ...createDefaultCohortMetricEntry(),
        filters: [...inlineFilters, ...additionalFilters],
        filtersOperator,
      });
    }

    cohort.groups = groups;
    // Apply event filters for common condition
    /** @todo: [pablo] Use the same hasUniformResourceTypes approach for query simplification */
  } else if (
    filtersOperator === FiltersOperator.And ||
    ((additionalFilters?.length === 0 || hasFirstTimeFilter) && hasUniformResourceTypes(inlineFilters))
  ) {
    cohort.groups = getCohortEntriesFromBehavior(behavior, {
      label,
      filters: [...inlineFilters, ...additionalFilters],
      filtersOperator,
      entryFiltersOperator: filtersOperator,
      frequency,
      frequencyExclusive,
      dataGroupId,
    });
  } else if (filtersOperator === FiltersOperator.Or) {
    // Create n entries for or-ed filters with a and-ed segment filter

    /**
     * @TODO pablo - this has an inherent bug for frequency analysis where splitting logical ORs into ANDs
     * is not performing the same behavior count, it's not the same to count Event A and (B or C) and to
     * count (Event A and B) or (Event A and C), they are logically equivalent but the counts do not reflect
     * the intended behavior
     */
    cohort.groups = inlineFilters.reduce(
      (entries, filter) => [
        ...entries.map((entry, i) => {
          return i === entries.length - 1
            ? {
                ...entry,
                groupingOperator: FiltersOperator.Or,
              }
            : entry;
        }),
        ...getCohortEntriesFromBehavior(behavior, {
          label,
          filters: [filter, ...additionalFilters],
          filtersOperator: FiltersOperator.And,
          groupingOperator: FiltersOperator.And,
          frequency,
          frequencyExclusive,
          dataGroupId,
        }),
      ],
      [],
    );
  } else {
    throw new Error(`Invalid filtersOperator ${filtersOperator}, only and/or are supported for translation`);
  }

  return cohort;
}
